import { memo } from "react";
import { Handle, Position } from "reactflow";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { blue, green, amber, grey, purple, deepOrange } from "@mui/material/colors";
import { FS } from "@plannel/utils/font-util";

/**
 * FLO 차트 커스텀 노드.
 * - 품목(Item)    : 삼각형
 * - 라우트(Route) : 사각형
 * - 자원(Resource): 타원
 * 정션(연결 의미) 노드 — 일반 노드보다 훨씬 작게 표시:
 * - 결합(Combined, AND) : 다이아몬드(◇) — 모든 입력을 함께 소비
 * - 택일(Alternate, OR) : 원(○)        — 입력 중 하나를 선택
 *
 * 핸들 위치/추가 핸들은 buildFloGraph가 노드별로 계산해 data에 담아 전달한다.
 * (data.targetPos / data.sourcePos / data.extraHandles)
 */

const handleStyle = { opacity: 0, width: 1, height: 1, border: "none" };

// 모든 노드를 동일한 정사각 코어에 모양을 중앙 정렬해 핸들(연결점) 위치를 통일한다.
// → 모양 높이가 달라도 핸들이 항상 코어 중앙에 와서 주 흐름 선이 직선이 된다.
const CORE = 64;

// 완제품이 LR=오른쪽 / TB=위에 오도록: TB는 입력=아래, 출력=위 (buildFloGraph의 MAIN_T/MAIN_S와 일치).
const fallbackPos = (direction, which) => {
  if (direction === "LR") return which === "target" ? Position.Left : Position.Right;
  return which === "target" ? Position.Bottom : Position.Top;
};

const Handles = ({ data }) => (
  <>
    <Handle type="target" position={data.targetPos ?? fallbackPos(data.direction, "target")} style={handleStyle} />
    <Handle type="source" position={data.sourcePos ?? fallbackPos(data.direction, "source")} style={handleStyle} />
    {(data.extraHandles ?? []).map((h) => (
      <Handle key={h.id} id={h.id} type={h.type} position={h.position} style={handleStyle} />
    ))}
  </>
);

// 라벨이 있는 일반 노드(품목·라우트·자원). 라벨은 코어 밖(아래)에 절대배치해 핸들 위치에 영향 주지 않음.
const Shell = ({ data, selected, shapeSx, shapeTitle }) => (
  <Box
    sx={{
      position: "relative",
      width: CORE,
      height: CORE,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "8px",
      outline: selected ? `2px dashed ${purple[400]}` : "none",
      outlineOffset: "2px",
    }}
  >
    <Handles data={data} />
    <Box sx={shapeSx} title={shapeTitle} />
    {/* 동일 원본이 여러 곳에 전개됐음을 알리는 배지(순번/총개수). hover 시 전체가 함께 강조된다. */}
    {data.dupTotal > 1 && (
      <Box
        className="flo-dup-badge"
        title={`동일 항목이 ${data.dupTotal}곳에 전개됨 (${data.dupIndex}/${data.dupTotal})`}
        sx={{
          position: "absolute",
          top: -7,
          right: -7,
          minWidth: 16,
          height: 16,
          px: "3px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          border: `1px solid ${deepOrange[600]}`, // 외곽선은 진하게
          backgroundColor: deepOrange[100], // 내부는 옅게
          color: "#000", // 글자는 검정
          fontSize: FS.CAPTION,
          fontWeight: 700,
          lineHeight: 1,
          boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
          pointerEvents: "none",
        }}
      >
        {data.dupIndex}/{data.dupTotal}
      </Box>
    )}
    {/* BOM 소비 비율(x{qty}) — 코어 왼쪽 위 코너 배지(구성품 품목에만). 노드 footprint 안이라 결합 박스 경계와 안 겹침. */}
    {data.ratio != null && (
      <Box
        className="flo-ratio-badge"
        sx={{
          position: "absolute",
          top: -6,
          left: -6,
          px: "3px",
          height: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          border: `1px solid ${green[600]}`, // 외곽선은 진하게
          backgroundColor: green[100], // 내부는 옅게
          color: "#000", // 글자는 검정
          fontSize: FS.CAPTION,
          fontWeight: 700,
          lineHeight: 1,
          boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
          pointerEvents: "none",
        }}
      >
        x{data.ratio}
      </Box>
    )}
    {/* 이름 — 코어 아래. 흰 배경 칩으로 뒤의 연결선을 가려 겹쳐 보이지 않게 한다(특히 TB에서 라우트 이름). */}
    <Typography
      sx={{
        position: "absolute",
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        mt: "2px",
        px: "3px",
        fontSize: FS.BODY,
        fontWeight: 600,
        color: grey[800],
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        backgroundColor: "rgba(255,255,255,0.85)",
        borderRadius: "3px",
      }}
    >
      {data.label}
    </Typography>
  </Box>
);

// 이름 라벨이 없는 작은 정션 노드(결합/택일) — 동일 코어 크기로 핸들 정렬.
// data.label(대체 공정 ○ 의 "OR")이 있으면 도형 옆에 작게 표시한다. 연결선이 도형을 주 흐름 방향으로
// 지나가므로 선과 겹치지 않게 흐름의 수직 방향에 둔다(LR=도형 위 / TB=도형 왼쪽).
const JunctionShell = ({ data, shapeSx }) => (
  <Box sx={{ position: "relative", width: CORE, height: CORE, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <Handles data={data} />
    <Box sx={shapeSx} />
    {data.label && (
      <Box
        sx={{
          position: "absolute",
          ...(data.direction === "LR"
            ? { bottom: "calc(50% + 10px)", left: "50%", transform: "translateX(-50%)" }
            : { right: "calc(50% + 12px)", top: "50%", transform: "translateY(-50%)" }),
          fontSize: FS.CAPTION,
          fontWeight: 700,
          lineHeight: 1,
          color: grey[700],
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}
      >
        {data.label}
      </Box>
    )}
  </Box>
);

// 대체 자재(z_alt_supply) 품목 — 같은 삼각형이지만 사선 해치 채움으로 "조건부 대체"를 구분한다.
// clip-path 요소라 테두리를 점선으로 줄 수 없으므로(잘림) 채움 패턴으로 표현한다.
const ALT_SUPPLY_FILL = `repeating-linear-gradient(45deg, ${green[100]} 0 4px, ${green[400]} 4px 8px)`;

const ItemNode = memo(({ data, selected }) => {
  const { t } = useTranslation();
  return (
    <Shell
      data={data}
      selected={selected}
      shapeTitle={data.isAltSupply ? t("floAltSupplyOf", { item: data.altSupplyOf ?? "" }) : undefined}
      shapeSx={{
        width: 56,
        height: 48,
        clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)",
        backgroundColor: green[300],
        ...(data.isAltSupply && { backgroundImage: ALT_SUPPLY_FILL }),
        // clip-path 요소는 border/box-shadow가 잘리므로 외곽선·그림자를 drop-shadow로 표현
        filter: `drop-shadow(0 0 1px ${green[800]}) drop-shadow(0 2px 3px rgba(0,0,0,0.18))`,
      }}
    />
  );
});

const RouteNode = memo(({ data, selected }) => (
  <Shell
    data={data}
    selected={selected}
    shapeSx={{
      width: 64,
      height: 40,
      borderRadius: "8px",
      border: `2px solid ${blue[600]}`,
      backgroundColor: blue[100],
      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
    }}
  />
));

const ResourceNode = memo(({ data, selected }) => (
  <Shell
    data={data}
    selected={selected}
    shapeSx={{
      width: 64,
      height: 40,
      borderRadius: "50%", // 타원(oval)
      border: `2px solid ${amber[700]}`,
      backgroundColor: amber[100],
      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
      cursor: data.collapsed || data.collapsible ? "pointer" : undefined,
    }}
  />
));

// 결합(AND) — 작은 다이아몬드
const CombinedNode = memo(({ data }) => (
  <JunctionShell
    data={data}
    shapeSx={{
      width: 16,
      height: 16,
      clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
      backgroundColor: grey[600],
      filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.25))",
    }}
  />
));

// 택일(OR) — 작은 원
const AlternateNode = memo(({ data }) => (
  <JunctionShell
    data={data}
    shapeSx={{
      width: 14,
      height: 14,
      borderRadius: "50%",
      backgroundColor: grey[600],
      boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
      cursor: data.collapsible ? "pointer" : undefined, // 클릭 시 자원 접기
    }}
  />
));

// 단계(stage) 영역 — 다른 노드 뒤에 깔리는 반투명 배경 밴드(번호 없음).
// 배경색/테두리 등 시각 스타일은 scss(.flo-stage)에서, 크기만 sx로 지정.
const StageNode = memo(({ data }) => (
  <Box
    className={`flo-stage flo-stage--${data.parity === 0 ? "even" : "odd"}`}
    sx={{ width: data.width, height: data.height }}
  />
));

// 결합(AND) 그룹 — 구성품들을 감싸는 사각형. 다이아몬드(◇) 대체.
// 주 흐름 방향으로 단일 출력 핸들 하나만 둔다(박스 → 라우트/택1).
// 테두리/배경 스타일은 scss(.flo-combine), 크기만 sx로 지정.
// 그룹 박스 — 결합(실선, flo-combine) / 택1(점선, flo-alt) 공용.
// variant: "alt"면 점선(택1), 아니면 실선(결합). collapsible이면 클릭 가능(자원 접기).
// label: 박스 문구 — 대체 자원(택1)="OR" / 동시 자원·BOM 구성품(결합)="AND". 테두리 위 코너에 얹는다.
const CombineGroupNode = memo(({ data }) => (
  <Box
    className={data.variant === "alt" ? "flo-alt" : "flo-combine"}
    sx={{
      position: "relative",
      width: data.width,
      height: data.height,
      cursor: data.collapsible ? "pointer" : undefined,
      pointerEvents: data.collapsible ? "auto" : "none", // 비클릭 박스는 통과(내부 노드 선택 보장)
    }}
  >
    {data.label && <Box className="flo-group-label">{data.label}</Box>}
    <Handle type="target" position={data.targetPos ?? fallbackPos(data.direction, "target")} style={handleStyle} />
    <Handle type="source" position={data.sourcePos ?? fallbackPos(data.direction, "source")} style={handleStyle} />
  </Box>
));

// BOM 소비 비율(x{qty}) 라벨 — 구성품 옆에 표시(결합 박스 사용 시 엣지 라벨 대체).
const QtyLabelNode = memo(({ data }) => (
  <Box sx={{ pointerEvents: "none", fontSize: FS.BODY, fontWeight: 700, color: green[800], whiteSpace: "nowrap" }}>
    {data.label}
  </Box>
));

ItemNode.displayName = "ItemNode";
RouteNode.displayName = "RouteNode";
ResourceNode.displayName = "ResourceNode";
CombinedNode.displayName = "CombinedNode";
AlternateNode.displayName = "AlternateNode";
StageNode.displayName = "StageNode";
CombineGroupNode.displayName = "CombineGroupNode";
QtyLabelNode.displayName = "QtyLabelNode";

export const floNodeTypes = {
  itemNode: ItemNode,
  routeNode: RouteNode,
  resourceNode: ResourceNode,
  combinedNode: CombinedNode,
  alternateNode: AlternateNode,
  stageNode: StageNode,
  combineGroupNode: CombineGroupNode,
  qtyLabelNode: QtyLabelNode,
};

// 미니맵 노드 색상
export const floMinimapColor = (node) => {
  switch (node.type) {
    case "itemNode":
      return green[300];
    case "routeNode":
      return blue[300];
    case "resourceNode":
      return amber[400];
    case "combinedNode":
    case "alternateNode":
      return grey[500];
    case "stageNode":
    case "combineGroupNode":
    case "qtyLabelNode":
      return "transparent"; // 미니맵에는 표시하지 않음
    default:
      return grey[300];
  }
};
